import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, concatMap, finalize, forkJoin, map, Observable, of, shareReplay, tap } from 'rxjs';
import { MemberService } from './member.service';
import { Member } from '../models/user/member.model';
import { PLAYER_SPACE_ROLES, RolesEnum } from '../models/user/roles.enum';
import { ApiRoutesService } from './api-routes.service';
import { JwtResponse } from '../models/jwt-response.model';
import { STORAGE_CONSTANTS } from '../utils/storage.constants';
import { Organization } from '../models/user/organization.model';
import { Router } from '@angular/router';
import { SessionService } from './session.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  readonly #httpClient = inject(HttpClient);
  readonly #router = inject(Router);
  readonly #membersService = inject(MemberService);
  readonly #sessionService = inject(SessionService);
  readonly #routes = inject(ApiRoutesService);

  #member: Member | null = null;
  // Source unique du tenant sélectionné. `_tenant` n'en est que le miroir, relu
  // au démarrage pour amorcer l'affichage avant la réponse du serveur.
  readonly #tenant = signal<Organization | null>(null);
  #tenants: Organization[] | null = null;
  // Requête de relecture en vol, partagée : deux gardes d'une même route
  // s'exécutent en parallèle et ne doivent pas doubler l'appel.
  #tenantRefresh$: Observable<Organization | null> | null = null;

  authenticate(login: string, password: string): Observable<JwtResponse> {
    return this.#httpClient.post<JwtResponse>(this.#routes.login(), {login, password})
      .pipe(concatMap((token) => this.#openSession(token)));
  }

  // Toute ouverture de session : token, membre, puis profils des tenants (flags).
  #openSession(token: JwtResponse): Observable<JwtResponse> {
    this.handleToken(token);

    return this.#membersService.me().pipe(
      tap((member) => this.#member = member),
      concatMap(() => token.tenants && token.tenants.length > 0 ? this.loadTenantProfiles() : of([])),
      map(() => token)
    );
  }

  public loadTenantProfiles(): Observable<Organization[]> {
    if (!this.#tenants || this.#tenants.length === 0) {
      return of([]);
    }

    return forkJoin(
      this.#tenants.map(t => this.#httpClient.get<Organization>(this.#routes.organization(t.id)))
    ).pipe(
      tap((profiles) => {
        this.#tenants = profiles;
        if (profiles.length === 1) {
          this.#setTenant(profiles[0]);
        }
      })
    );
  }

  /**
   * Relit le profil du tenant sélectionné (flags, quotas, CGVU…) : l'admin a pu
   * le modifier depuis la connexion. Appelé au démarrage, par les gardes à chaque
   * navigation et au retour sur l'onglet. Un échec garde la valeur connue : un
   * incident réseau ne doit ni déconnecter ni bloquer.
   */
  public refreshTenant(): Observable<Organization | null> {
    const current = this.#tenant();
    if (!current?.id || !this.isAuthenticated) {
      return of(current);
    }

    if (!this.#tenantRefresh$) {
      this.#tenantRefresh$ = this.#httpClient.get<Organization>(this.#routes.organization(current.id)).pipe(
        tap((fresh) => {
          // Changement de tenant pendant le vol : la réponse ne le concerne plus.
          if (fresh?.id === current.id && this.#tenant()?.id === current.id) {
            this.#setTenant(fresh);
            this.#tenants = this.#tenants?.map(t => t.id === fresh.id ? fresh : t) ?? null;
          }
        }),
        map(() => this.#tenant()),
        catchError(() => of(this.#tenant())),
        finalize(() => this.#tenantRefresh$ = null),
        shareReplay({bufferSize: 1, refCount: false})
      );
    }
    return this.#tenantRefresh$;
  }

  switchTenant(tenant: Organization): Observable<JwtResponse> {
    this.#setTenant(tenant);

    return this.#httpClient.post<JwtResponse>(this.#routes.switchTenant(), tenant.id).pipe(
      tap((token: JwtResponse) => {
        this.handleToken(token);
      })
    );
  }

  authenticateCodeValidation(token: JwtResponse) {
    this.handleToken(token);
    this.#membersService.me().subscribe((member) => this.#member = member);
  }

  /**
   * Définir son mot de passe (invitation ou oubli) ouvre une session : même
   * pipeline que le login. Sans les profils des tenants, la première session
   * d'un compte activé n'avait aucun flag, donc ni Studio ni Membres.
   */
  public resetPassword(code: string, memberId: string, password: string): Observable<JwtResponse> {
    return this.#httpClient.put<JwtResponse>(this.#routes.register() + '/password', {code, memberId, password})
      .pipe(concatMap((token) => this.#openSession(token)));
  }

  routeFromReload() {
    if (this.isAdmin) {
      this.#router.navigate(['/admin']);
    } else if (this.isOperator) {
        this.#router.navigate(['/operator']);
    } else {
      this.#router.navigate(['/app/game']);
    }
  }

  routeByLogin(jwtResponse: JwtResponse) {
    if (this.isAdmin) {
      this.#router.navigate(['/admin']);
    } else if (this.isOperator) {
      if (jwtResponse.tenants == null || jwtResponse.tenants.length === 0) {
        this.logout();
      } else if (jwtResponse.tenants.length > 1) {
        this.#router.navigate(['/operator/selection']);
      } else {
        this.#router.navigate(['/operator']);
      }
    } else {
      this.#router.navigate(['/app/game']);
    }
  }

  private handleToken(token: JwtResponse | null): void {
    if (token != null) {
      this.#tenants = token.tenants;
      const storedTenant = this.#readStoredTenant();

      if (storedTenant != null) {
        this.#tenant.set(storedTenant);
      }

      localStorage.setItem(STORAGE_CONSTANTS.TOKEN_NAME, JSON.stringify(token));
    } else {
      localStorage.clear();
    }
  }

  // `localStorage` est une frontière non fiable : valeur corrompue ou héritée
  // d'une ancienne version ⇒ on purge et on repart sans tenant.
  #readStoredTenant(): Organization | null {
    try {
      const raw = localStorage.getItem(STORAGE_CONSTANTS.SELECTED_TENANT);
      return raw ? JSON.parse(raw) : null;
    } catch {
      try {
        localStorage.removeItem(STORAGE_CONSTANTS.SELECTED_TENANT);
      } catch {
        // stockage inaccessible : rien à purger
      }
      return null;
    }
  }

  #setTenant(tenant: Organization | null): void {
    this.#tenant.set(tenant);
    try {
      if (tenant) {
        localStorage.setItem(STORAGE_CONSTANTS.SELECTED_TENANT, JSON.stringify(tenant));
      } else {
        localStorage.removeItem(STORAGE_CONSTANTS.SELECTED_TENANT);
      }
    } catch {
      // Stockage indisponible (navigation privée, quota) : le signal reste la vérité.
    }
  }

  public get token(): JwtResponse | null {
    const token = localStorage.getItem(STORAGE_CONSTANTS.TOKEN_NAME);

    if (!token) {
      return null;
    }

    try {
      return JSON.parse(token);
    } catch {
      // jeton hérité (JWT brut, ancien format) : on purge, l'utilisateur se reconnectera
      localStorage.removeItem(STORAGE_CONSTANTS.TOKEN_NAME);
      return null;
    }
  }

  public get accessToken() {
    return this.token ? this.token.accessToken : null;
  }

  public get refreshToken() {
    return this.token ? this.token.refreshToken : null;
  }

  public get roles(): string[] | [] {
    return this.token ? this.token.roles : [];
  }

  public get isAuthenticated(): boolean {
    return !!this.token;
  }

  public get isMember(): boolean {
    return this.roles.find((role: string) => role === RolesEnum.ROLE_USER) != null;
  }

  /** La règle de la garde de `/app` : pour tout autre visiteur, l'espace joueur renvoie à la connexion. */
  public get canOpenPlayerSpace(): boolean {
    return this.roles.some((role: string) => PLAYER_SPACE_ROLES.includes(role));
  }

  public get isOperator(): boolean {
    return this.roles.find((role: string) => role === RolesEnum.ROLE_OPERATOR) != null;
  }

  public get isAdmin(): boolean {
    return this.roles.find((role: string) => role === RolesEnum.ROLE_CLIENT_ADMIN || role === RolesEnum.ROLE_ADMIN) != null;
  }

  public get isSuperAdmin(): boolean {
    return this.roles.find((role: string) => role === RolesEnum.ROLE_ADMIN) != null;
  }

  public get member(): Member | null {
    return this.#member;
  }

  get tenant(): Organization | null {
    return this.#tenant();
  }

  get tenants(): Organization[] | null {
    return this.#tenants;
  }

  /**
   * Seul l'exploitant a une ligne de session côté serveur (sessions simultanées) :
   * on la libère en best-effort AVANT de vider le stockage, sinon elle comptait
   * encore et la connexion suivante annonçait une session ouverte ailleurs.
   * L'échec de cet appel ne bloque jamais la déconnexion.
   */
  logout() {
    const accessToken = this.accessToken;
    if (accessToken && this.isOperator) {
      this.#sessionService.release(accessToken).subscribe({error: () => {}});
    }

    localStorage.clear();
    this.#member = null;
    this.#tenant.set(null);
    this.#tenants = null;
  }

  public initializeApp(): Observable<any> {
    const token = this.token;

    if (token) {
      this.handleToken(token);
    }

    return this.#membersService.me().pipe(
      tap((member) => {
        this.#member = member;
      }),
      // `_tenant` date du login : au F5, un flag changé par l'admin doit
      // s'appliquer sans reconnexion. L'échec garde le snapshot (cf. refreshTenant).
      concatMap((member) => this.refreshTenant().pipe(map(() => member)))
    );
  }
}
